import React, { useEffect, useMemo, useState } from 'react';
import {
  filter, merge, Subject, takeUntil, tap,
} from 'rxjs';
import useStyles from 'isomorphic-style-loader/useStyles';
import Lottie from 'lottie-react';

import { Container } from '../../../../symphony';

import { AnswerType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { SlDataService } from '../../services/sl-data.service';
import { IQuizFeedback, QuizFeedbackType } from '../../global-interface';
import { getQuizFeedbacks, SlLEContext } from '../../global-constants';
import { Kodie } from '../Kodie/Kodie';

import s from './QuizFeedback.scss';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';

export function QuizFeedback(): JSX.Element {
  useStyles(s);
  const [data, setData] = useState<IQuizFeedback>(null);
  const [answerType, setAnswerType] = useState<AnswerType>(null);
  const [animation, setAnimation] = useState(null);
  const {
    quizFeedbackFromBackend$: quizFeedbackFromBackend,
    quizFeedback$: quizFeedback,
  } = useContainerData(SlLEContext, SlDataService, ['quizFeedbackFromBackend$', 'quizFeedback$']);

  useEffect(() => {
    const { quizFeedback$, question$ } = Container.take(SlLEContext, SlDataService);

    const unsubscriber = new Subject<void>();

    merge(
      quizFeedback$.pipe(tap((type) => {
        setData(type ? getQuizFeedbacks()[type] : null);
      })),
      question$.pipe(
        filter((q) => !!q),
        tap((question) => setAnswerType(question?.answer?.answerTypeId)),
      ),
    )
      .pipe(takeUntil(unsubscriber))
      .subscribe();

    return () => {
      unsubscriber.next();
      unsubscriber.complete();
    };
  }, []);

  useEffect(() => {
    if (data?.getAnimation) {
      data.getAnimation().then(setAnimation);
    } else {
      setAnimation(null);
    }
  }, [data]);

  const feedBackTitle = useMemo(() => {
    if (
      ([
        QuizFeedbackType.Correct3InARow,
        QuizFeedbackType.Correct5InARow,
        QuizFeedbackType.FewOptions,
      ].includes(quizFeedback))
      || !quizFeedbackFromBackend
    ) {
      return data ? data.getTitle() : 'no title';
    }

    return quizFeedbackFromBackend;
  }, [data, quizFeedbackFromBackend, quizFeedback]);

  return data ? (
    <div
      className={`le-quiz-feedback ${data.type}`}
      sl-test-data="cmpQuizFeedback"
    >
      {animation && (
        <div
          className="le-quiz-feedback__animation"
          style={animation.style}
        >
          <Lottie
            animationData={animation.data}
            autoPlay
            loop
          />
        </div>
      )}
      <div className="le-quiz-feedback__text">{feedBackTitle}</div>
      <Kodie isAnswerExplanation />
    </div>
  ) : null;
}
